import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';

import { Carousel } from '@mantine/carousel';
import { Skeleton } from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import { selectPasses } from '@/store/user-process/user-selectors.ts';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';
import { BookContext } from '@/contexts/BookContext/BookContextProvider';

import { bookingObjectsApi } from '@/shared/api/bookingObjects';
import { BOOKING_TYPES, TODAY } from '@/shared/constant/app';
import { calcObjectLoad, resetTime } from '@/shared/helpers/utils';
import { useGetBookingTypeData } from '@/shared/hooks/useGetBookingTypeData';
import useBookingStore from '@/shared/store/booking.store.ts';
import { useSettingsStore } from '@/shared/store/settings.store.ts';
import { TDateRange } from '@/shared/types/app.types';
import { BookingType } from '@/shared/types/bookingObjects.types';
import { ContentCheckbox } from '@/shared/uikit/components/checkbox/contentCheckbox';

import { useAppSelector } from '@/hooks';

import { exclusiveEnd } from '@/utils/date';

type BookingTypeSliderProps = {
	initialType: BookingType;
	showLoad?: boolean;
	date?: TDateRange;
	disabled?: boolean;
	onChange?: (bookingType: BookingType) => void;
};

export const BookingTypeSlider = ({
	initialType,
	showLoad,
	date,
	disabled = false,
	onChange,
}: BookingTypeSliderProps) => {
	const [currentType, setCurrentType] = useState(initialType);
	const [currentSlide, setCurrentSlide] = useState(BOOKING_TYPES?.indexOf(currentType));
	const location = useSettingsStore((state) => state.location);
	const bookingLocation = useBookingStore((state) => state.bookingLocation);
	const setBookingPlace = useBookingStore((state) => state.setBookingPlace);
	const { setCurrentBookingLoad } = useContext(BookContext);
	const bookingTypes = BOOKING_TYPES?.map((type) => useGetBookingTypeData({ bookingType: type }));
	const passes = useAppSelector(selectPasses);

	const { data: objectLoad, isLoading: isLoadingObjectLoad } = useQuery({
		queryKey: ['currentBookingLoad', date, bookingLocation, currentType],
		queryFn: () =>
			bookingLocation && date
				? bookingObjectsApi.getObjectLoad(bookingLocation?.id, [
						date[0],
						exclusiveEnd(date[1]),
					])
				: null,
		enabled: showLoad,
	});

	const { data: parkingLoad } = useQuery({
		queryKey: ['currentParkingLoad', date, location, bookingLocation, currentType],
		queryFn: () =>
			location && bookingLocation && date
				? bookingObjectsApi.getObjectLoad(
						// location?.parking.length < 2 ? location?.parking[0].id : bookingLocation?.id,
						bookingLocation ? bookingLocation?.id : location?.parking[0].id,
						[date[0], exclusiveEnd(date[1])],
					)
				: null,
		enabled: showLoad,
	});

	const handleChangeBookingType = (bookingType: BookingType, idx: number) => {
		if (idx === currentSlide || !onChange) return;

		setCurrentType(bookingType);
		setCurrentSlide(idx);
		setBookingPlace(null);
		onChange(bookingType);
	};

	useEffect(() => {
		if (objectLoad && parkingLoad) {
			setCurrentBookingLoad(
				Number(
					initialType === 'parking_place'
						? calcObjectLoad(
								parkingLoad[initialType]?.booked ?? 0,
								parkingLoad[initialType]?.total ?? 0,
							)
						: calcObjectLoad(
								objectLoad[initialType]?.booked ?? 0,
								objectLoad[initialType]?.total ?? 0,
							),
				),
			);
		}
	}, [objectLoad, parkingLoad]);

	return (
		<Carousel
			align="start"
			slidesToScroll="auto"
			slideSize="auto"
			slideGap={8}
			initialSlide={currentSlide}
			w="100%"
			withControls={false}
			style={disabled ? { pointerEvents: 'none' } : {}}
			loop
		>
			{bookingTypes?.map((item, idx) => {
				if (
					(item.type === 'parking_place' &&
						!passes?.find((pass) => pass.pass_type.kind === 'car')) ||
					(item.type === 'parking_place' &&
						resetTime(dayjs(date?.[1])).diff(resetTime(TODAY), 'day') > 14)
				)
					return;

				return (
					<Carousel.Slide
						key={item?.label}
						onClick={() => handleChangeBookingType(item?.type as BookingType, idx)}
						//TODO: добавить мультибронь
						{...(item?.type === 'work_park'
							? {
									style: {
										// pointerEvents: 'none',
										display: 'none',
									},
								}
							: {})}
					>
						<Skeleton visible={isLoadingObjectLoad}>
							<ContentCheckbox
								label={item?.label}
								leftIcon={item?.icon}
								{...(showLoad && objectLoad && parkingLoad
									? {
											subtitle:
												objectLoad[item.type] || parkingLoad[item.type]
													? `Загруженность ${String(
															item.type === 'parking_place'
																? calcObjectLoad(
																		parkingLoad[item.type]?.booked ?? 0,
																		parkingLoad[item.type]?.total ?? 0,
																	)
																: (calcObjectLoad(
																		objectLoad[item.type]?.booked ?? 0,
																		objectLoad[item.type]?.total ?? 0,
																	) ?? '0'),
														)}%`
													: 'Укажите данные',
										}
									: {})}
								checked={item?.type === currentType}
							/>
						</Skeleton>
					</Carousel.Slide>
				);
			})}
		</Carousel>
	);
};
